import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Typography,
  Paper,
  LinearProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Transaction, Budget, Category } from '../types';

interface BudgetAlert {
  id: string;
  type: 'warning' | 'danger' | 'info';
  category: string;
  message: string;
  percentage: number;
  spent: number;
  budget: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface BudgetAlertsProps {
  transactions: Transaction[];
  budgets: Budget[];
  categories: Category[];
}

const BudgetAlerts: React.FC<BudgetAlertsProps> = ({ transactions, budgets, categories }) => {
  const [alerts, setAlerts] = useState<BudgetAlert[]>([]);
  const [expanded, setExpanded] = useState(true);

  // Calculer les dépenses par catégorie pour le mois actuel
  const calculateMonthlySpending = useCallback(() => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const monthlySpending: { [category: string]: number } = {};

    transactions
      .filter(t => {
        const transactionDate = new Date(t.date);
        return (
          t.type === 'expense' &&
          transactionDate.getMonth() === currentMonth &&
          transactionDate.getFullYear() === currentYear
        );
      })
      .forEach(t => {
        monthlySpending[t.category] = (monthlySpending[t.category] || 0) + t.amount;
      });

    return monthlySpending;
  }, [transactions]);

  // Générer les alertes basées sur les budgets
  const generateAlerts = useCallback(() => {
    const monthlySpending = calculateMonthlySpending();
    const newAlerts: BudgetAlert[] = [];

    budgets.forEach(budget => {
      const spent = monthlySpending[budget.category] || 0;
      const percentage = budget.allocated > 0 ? (spent / budget.allocated) * 100 : 0;

      let alertType: 'warning' | 'danger' | 'info' = 'info';
      let severity: 'low' | 'medium' | 'high' | 'critical' = 'low';
      let message = '';

      if (percentage >= 100) {
        alertType = 'danger';
        severity = 'critical';
        const overspent = spent - budget.allocated;
        message = `Budget dépassé de ${overspent.toFixed(2)}€ !`;
      } else if (percentage >= 90) {
        alertType = 'danger';
        severity = 'high';
        const remaining = budget.allocated - spent;
        message = `Plus que ${remaining.toFixed(2)}€ disponibles (${(100 - percentage).toFixed(1)}%)`;
      } else if (percentage >= 75) {
        alertType = 'warning';
        severity = 'medium';
        message = `Attention, 75% du budget utilisé`;
      } else if (percentage >= 50) {
        alertType = 'info';
        severity = 'low';
        message = `Mi-budget atteint (${percentage.toFixed(1)}%)`;
      }

      if (percentage >= 50) {
        newAlerts.push({
          id: `alert-${budget.category}`,
          type: alertType,
          category: budget.category,
          message,
          percentage,
          spent,
          budget: budget.allocated,
          severity,
        });
      }
    });

    // Trier par criticité
    newAlerts.sort((a, b) => {
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });

    setAlerts(newAlerts);
  }, [budgets, calculateMonthlySpending]);

  useEffect(() => {
    generateAlerts();
  }, [generateAlerts]);

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'danger': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'danger': return 'error';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#fbc02d';
      default: return '#1976d2';
    }
  };

  if (alerts.length === 0) {
    return (
      <Paper sx={{ 
        p: 2, 
        mb: 3, 
        backgroundColor: 'success.main', 
        color: 'white',
        '& .MuiSvgIcon-root': { color: 'white' }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1 }} />
          <Typography variant="body1" fontWeight="medium">
            🎉 Tous vos budgets sont sous contrôle !
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mb: 3 }}>
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ErrorIcon color="error" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            Alertes Budget ({alerts.length})
          </Typography>
          <Chip 
            label={alerts.filter(a => a.severity === 'critical').length + ' critiques'}
            color="error"
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>
        <IconButton>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          {alerts.map((alert) => (
            <Alert 
              key={alert.id}
              severity={getAlertColor(alert.type) as any}
              icon={getAlertIcon(alert.type)}
              sx={{ mb: 2 }}
            >
              <AlertTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{alert.category}</span>
                <Chip 
                  label={alert.severity.toUpperCase()}
                  size="small"
                  sx={{ 
                    backgroundColor: getSeverityColor(alert.severity),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </AlertTitle>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                {alert.message}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(alert.percentage, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: alert.percentage >= 100 ? '#d32f2f' : 
                                       alert.percentage >= 90 ? '#f57c00' : 
                                       alert.percentage >= 75 ? '#fbc02d' : '#1976d2'
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 60 }}>
                  {alert.percentage.toFixed(1)}%
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption">
                  Dépensé: {alert.spent.toFixed(2)}€
                </Typography>
                <Typography variant="caption">
                  Budget: {alert.budget.toFixed(2)}€
                </Typography>
              </Box>
            </Alert>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default BudgetAlerts;
